                                                                                                                                                                                                                                                                                                                                                                                                                                                                                    import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import wholesaleProducts from '../data/wholesaleProducts';

const categoryDescriptions = {
  'Red Chillies': 'Selected bulk red chillies with strong pungency and rich natural color, suitable for food manufacturing, spice blending, and export-grade wholesale procurement.',
  Turmeric: 'High-curcumin turmeric sourced for consistent quality and color, ideal for processors, traders, and bulk packaging requirements.',
  Saffron: 'Premium-grade saffron strands known for aroma, color, and purity, suitable for high-value culinary and specialty wholesale channels.',
  Cardamom: 'Aromatic cardamom with clean pods and strong essential oil profile, preferred for masala blending and bulk spice distribution.',
  Ginger: 'Dry ginger variants selected for potency and flavor profile, supporting large-volume use in food, beverage, and traditional formulations.',
  'Seeds & Spices': 'Whole spice and seed lots curated for clean quality and stable shelf life, suited for wholesale repacking and industrial spice use.',
};

const WholesaleProductDetailPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();

  const product = useMemo(
    () => wholesaleProducts.find((item) => String(item.id) === String(productId)),
    [productId]
  );
  const productImages = product?.images?.length ? product.images : [product?.image].filter(Boolean);
  const [selectedImage, setSelectedImage] = useState('');

  useEffect(() => {
    setSelectedImage(productImages[0] || '');
  }, [productId, productImages]);

  if (!product) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)), url(/img/23.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box sx={{ textAlign: 'center', p: 4, backgroundColor: 'rgba(8,8,8,0.8)', borderRadius: '12px' }}>
          <Typography sx={{ fontFamily: 'Oswald', fontSize: '32px', color: '#FFFFFF', mb: 2 }}>
            PRODUCT NOT FOUND
          </Typography>
          <Button
            onClick={() => navigate('/products/wholesale')}
            sx={{
              fontFamily: 'Oswald',
              color: '#FFFFFF',
              backgroundColor: '#2858A8',
              px: 3,
              py: 1,
              '&:hover': { backgroundColor: '#1A3A6E' },
            }}
          >
            Back to Wholesale Products
          </Button>
        </Box>
      </Box>
    );
  }

  const description = product.description || categoryDescriptions[product.category] || 'This product is available for verified wholesale and bulk procurement.';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.35), rgba(0, 0, 0, 0.35)), url(/img/23.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        px: { xs: 3, md: 6, lg: 10 },
        py: { xs: 3, md: 5 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mb: 3 }}>
        <Box
          onClick={() => navigate('/products/wholesale')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, md: 2 },
            cursor: 'pointer',
            backgroundColor: 'rgba(216, 216, 188, 0.8)',
            backdropFilter: 'blur(8px)',
            px: { xs: 1.5, md: 2 },
            py: { xs: 0.8, md: 1 },
            borderRadius: '8px',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#1A1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <Typography sx={{ fontFamily: 'Roboto', fontSize: { xs: '14px', md: '16px' }, fontWeight: 500, color: '#1A1A1A', whiteSpace: 'nowrap' }}>
            Back to Wholesale
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 4,
          alignItems: 'start',
        }}
      >
        <Box>
          <Box
            sx={{
              width: '100%',
              minHeight: { xs: '260px', md: '420px' },
              backgroundImage: `url(${selectedImage || product.image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.15)',
              boxShadow: '0 8px 30px rgba(0,0,0,0.35)',
            }}
          />

          <Box sx={{ display: 'flex', gap: 1.2, mt: 1.5, flexWrap: 'wrap' }}>
            {productImages.map((imageUrl, index) => {
              const isActive = imageUrl === (selectedImage || product.image);
              return (
                <Box
                  key={`${product.id}-img-${index}`}
                  onClick={() => setSelectedImage(imageUrl)}
                  sx={{
                    width: '72px',
                    height: '72px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    backgroundImage: `url(${imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: isActive ? '2px solid #D4C5B0' : '2px solid rgba(255,255,255,0.25)',
                    boxShadow: isActive ? '0 0 0 1px rgba(212,197,176,0.5)' : 'none',
                  }}
                />
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: 'rgba(8,8,8,0.78)',
            borderRadius: '12px',
            p: { xs: 3, md: 4 },
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <Typography sx={{ fontFamily: 'Oswald', fontSize: { xs: '36px', md: '52px' }, fontWeight: 700, color: '#FFFFFF', lineHeight: 0.95 }}>
            {product.name.toUpperCase()}
          </Typography>
          <Typography sx={{ fontFamily: 'Roboto', fontSize: '14px', color: '#A8C5E8', mt: 1, mb: 3, letterSpacing: '0.08em' }}>
            {product.category.toUpperCase()}
          </Typography>

          <Typography sx={{ fontFamily: 'Roboto', fontSize: { xs: '14px', md: '16px' }, color: '#D4C5B0', lineHeight: 1.8, mb: 3 }}>
            {description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default WholesaleProductDetailPage;
